import { typographyTokens } from '../../../../theme/typographyTokens';
/**
 * SEPViewModal Component
 * 
 * Read-only modal dialog displaying comprehensive SEP details.
 * Organized into sections: SEP Information, Dates, Requestor, and Contact Information.
 * 
 * @module SEPViewModal
 * @path src/frontend/features/Admin/Others/SEPDetails/SEPViewModal.tsx
 */
import React, { useMemo } from 'react';
import {
  Box,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  IconButton,
  Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { transformQuillListToNested, nestedListStyles } from '../../../../utils/quillHtmlTransform';

/**
 * Complete SEP data interface for view display
 * Includes all SEP fields from the API response
 */
export interface SEPDetailData {
  id: number;
  createdAt: string;
  updatedAt: string;
  deletedAt: string | null;
  createdBy: string;
  template: boolean;
  name: string;
  phase: string;
  apmID: string | null;
  description: string | null;
  locked: boolean;
  apmData: string | null;
  status: string;
  /** Comma-separated list of subscriber emails (may have leading comma) */
  subscribers: string;
  workFlowType: string;
  solutionArchitectEmail: string;
  itkeyManagerEmail: string;
  vpEmail: string;
  requestorDepartment: string;
  validationDate: string | null;
  enterpriceArchitectEmail: string;
  completedAt: string | null;
  startedAt: string | null;
  /** Creator user object with profile details */
  creator: {
    id: string;
    email: string;
    displayName: string;
    givenName: string;
    surname: string;
    department: string;
  };
}

/**
 * Props interface for SEPViewModal
 * @property {boolean} open - Controls modal visibility
 * @property {function} onClose - Callback to close the modal
 * @property {SEPDetailData | null} sepData - SEP data to display
 * @property {any} theme - MUI theme object for styling
 */
interface SEPViewModalProps {
  open: boolean;
  onClose: () => void;
  sepData: SEPDetailData | null;
  theme: any;
}

/**
 * Formats a date string for display (locale-aware)
 * @param {string | null} dateString - ISO date string or null
 * @returns {string} Formatted date/time string or '-' if null
 */
const formatDate = (dateString: string | null): string => {
  if (!dateString) return '-';
  return new Date(dateString).toLocaleString();
};

/**
 * Capitalizes the first letter of a string
 * @param {string | null | undefined} str - String to capitalize
 * @returns {string} Capitalized string or '-' if empty
 */
const capitalize = (str: string | null | undefined): string => {
  if (!str) return '-';
  return str.charAt(0).toUpperCase() + str.slice(1);
};

/**
 * DetailRow - Reusable component for displaying label-value pairs
 * Renders HTML content safely using dangerouslySetInnerHTML for description fields
 * 
 * @param {object} props - Component props
 * @param {string} props.label - Field label text
 * @param {string | null | undefined} props.value - Field value (can contain HTML)
 */
const DetailRow: React.FC<{ label: string; value: string | null | undefined }> = ({ label, value }) => (
  <Grid item xs={12} sm={6}>
    <Box sx={{ mb: 2 }}>
      <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: typographyTokens.fontWeights.medium }}>
        {label}
      </Typography>
      <Typography
        variant="body2"
        sx={{ mt: 0.5, wordBreak: 'break-word', ...nestedListStyles }}
        dangerouslySetInnerHTML={{ __html: transformQuillListToNested(value ?? '-') }}
      />
    </Box>
  </Grid>
);

/**
 * SEPViewModal - Read-only modal for viewing SEP details
 * 
 * Sections:
 * 1. SEP Information - Core SEP details (ID, name, phase, status, etc.)
 * 2. Dates - Timeline information (created, updated, started, validation, completed)
 * 3. Requestor Information - Creator/requestor user details
 * 4. Contact Information - Stakeholder emails and subscribers
 * 
 * @param {SEPViewModalProps} props - Component props
 * @returns {JSX.Element | null} The view modal or null if no data
 */
const SEPViewModal: React.FC<SEPViewModalProps> = ({ open, onClose, sepData, theme }) => {
  /**
   * Memoized formatted subscribers list
   * Handles edge case where subscribers string starts with comma
   * (removes first 2 chars if comma present, then joins with ', ')
   */
  const formattedSubscribers = useMemo(() => {
    if (!sepData?.subscribers) return '-';
    // Handle legacy format where subscribers starts with comma
    const subscriberStr = sepData.subscribers.includes(',')
      ? sepData.subscribers.slice(2).split(',').join(', ')
      : sepData.subscribers;
    return subscriberStr || '-';
  }, [sepData?.subscribers]);

  // Don't render if no data provided
  if (!sepData) return null;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: { borderRadius: 2 }
      }}
    >
      <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', pb: 1 }}>
        <Typography variant="h6" sx={{ fontWeight: typographyTokens.fontWeights.semibold }}>
          SEP Details - {sepData.id}
        </Typography>
        <IconButton onClick={onClose} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <Divider />
      <DialogContent sx={{ pt: 3 }}>
        {/* Section 1: SEP Information */}
        <Typography variant="subtitle1" sx={{ fontWeight: typographyTokens.fontWeights.semibold, mb: 2, color: theme.palette.primary.main }}>
          SEP Information
        </Typography>
        <Grid container spacing={1}>
          <DetailRow label="SEP ID" value={sepData.id?.toString()} />
          <DetailRow label="Name" value={sepData.name} />
          <DetailRow label="Phase" value={capitalize(sepData.phase)} />
          <DetailRow label="Status" value={capitalize(sepData.status)} />
          <DetailRow label="Workflow Type" value={sepData.workFlowType} />
          <DetailRow label="APM ID" value={sepData.apmID} />
          <DetailRow label="Description" value={sepData.description} />
          <DetailRow label="Locked" value={sepData.locked ? 'Yes' : 'No'} />
        </Grid>

        <Divider sx={{ my: 2 }} />

        {/* Section 2: Dates */}
        <Typography variant="subtitle1" sx={{ fontWeight: typographyTokens.fontWeights.semibold, mb: 2, color: theme.palette.primary.main }}>
          Dates
        </Typography>
        <Grid container spacing={1}>
          <DetailRow label="Created At" value={formatDate(sepData.createdAt)} />
          <DetailRow label="Updated At" value={formatDate(sepData.updatedAt)} />
          <DetailRow label="Started At" value={formatDate(sepData.startedAt)} />
          <DetailRow label="Validation Date" value={formatDate(sepData.validationDate)} />
          <DetailRow label="Completed At" value={formatDate(sepData.completedAt)} />
        </Grid>

        <Divider sx={{ my: 2 }} />

        {/* Section 3: Requestor Information */}
        <Typography variant="subtitle1" sx={{ fontWeight: typographyTokens.fontWeights.semibold, mb: 2, color: theme.palette.primary.main }}>
          Requestor Information
        </Typography>
        <Grid container spacing={1}>
          <DetailRow label="Requestor Name" value={sepData.creator?.displayName} />
          <DetailRow label="Requestor Email" value={sepData.creator?.email} />
          <DetailRow label="Requestor Given Name" value={sepData.creator?.givenName} />
          <DetailRow label="Requestor Surname" value={sepData.creator?.surname} />
          <DetailRow label="Requestor Department" value={sepData.creator?.department} />
        </Grid>

        <Divider sx={{ my: 2 }} />

        {/* Section 4: Contact Information */}
        <Typography variant="subtitle1" sx={{ fontWeight: typographyTokens.fontWeights.semibold, mb: 2, color: theme.palette.primary.main }}>
          Contact Information
        </Typography>
        <Grid container spacing={1}>
          <DetailRow label="Solution Architect Email" value={sepData.solutionArchitectEmail} />
          <DetailRow label="IT Key Manager Email" value={sepData.itkeyManagerEmail} />
          <DetailRow label="VP Email" value={sepData.vpEmail} />
          <DetailRow label="Enterprise Architect Email" value={sepData.enterpriceArchitectEmail} />
          <DetailRow label="Subscribers" value={formattedSubscribers} />
        </Grid>
      </DialogContent>
    </Dialog>
  );
};

export default SEPViewModal;
